import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import dw from './daywiseItinerary.module.css';

const emptyService = () => ({
  title: '',
  kind: 'Sightseeing',
  rateIndian: 0,
  rateForeign: 0,
  childRateIndian: 0,
  childRateForeign: 0,
  nationalityMode: 'separate',
  enabled: true,
});

const TRANSPORT_MODES = [
  'Cab · Hatchback',
  'Cab · Sedan',
  'Cab · SUV',
  'Cab · Tempo Traveller',
  'Bus',
  'Train',
  'Flight',
  'Charter',
  'Helicopter',
];

const nextLegId = (days) => {
  let max = 0;
  (days || []).forEach((d) => {
    const m = /^leg(\d+)$/i.exec(String(d.legId || '').trim());
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `leg${max + 1}`;
};

const emptyLegTransport = (legId, dayNumber) => ({
  context: 'leg',
  contextId: legId,
  dayNumber,
  mode: 'Cab · Sedan',
  chargeBasis: 'vehicle',
  capacity: 4,
  rateShared: 0,
  rateIndian: 0,
  rateForeign: 0,
  nationalityPricing: 'shared',
  enabled: true,
});

const DayEditor = ({
  day,
  dayIndex,
  totalDays,
  allDays = [],
  legTransports,
  localTransports,
  onChange,
  onPrev,
  onNext,
  onTransportsChange,
  allTransports,
  sightseeings = [],
  sightseeingsLoadHint = '',
}) => {
  const [pickSightseeingId, setPickSightseeingId] = useState('');
  const [catalogueActionHint, setCatalogueActionHint] = useState('');

  useEffect(() => {
    if (!day || !onTransportsChange || localTransports?.length) return;
    const ctxId = String(day.dayNumber);
    const exists = (allTransports || []).some(
      (x) => x.context === 'local' && x.contextId === ctxId && x.dayNumber === day.dayNumber
    );
    if (exists) return;
    onTransportsChange([
      ...(allTransports || []),
      {
        context: 'local',
        contextId: ctxId,
        dayNumber: day.dayNumber,
        mode: 'Cab · Sedan',
        chargeBasis: 'vehicle',
        capacity: 4,
        rateShared: 0,
        rateIndian: 0,
        rateForeign: 0,
        nationalityPricing: 'shared',
        enabled: true,
      },
    ]);
  }, [day, day?.dayNumber, localTransports?.length, allTransports, onTransportsChange]);

  if (!day) return null;

  const updateDay = (patch) => onChange({ ...day, ...patch });

  const updateService = (idx, patch) => {
    const services = [...(day.services || [])];
    services[idx] = { ...services[idx], ...patch };
    updateDay({ services });
  };

  const addService = (overrides = {}) => {
    updateDay({ services: [...(day.services || []), { ...emptyService(), ...overrides }] });
  };

  const addSightseeingFromCatalogue = (sightseeingId) => {
    const catalogueId = String(sightseeingId || '');
    if (!catalogueId) return false;
    const item = sightseeings.find((s) => String(s.id) === catalogueId);
    if (!item) {
      setCatalogueActionHint('Selected sightseeing was not found — refresh the page.');
      return false;
    }
    const already = (day.services || []).some(
      (svc) => svc.kind === 'Sightseeing' && String(svc.sightseeingCatalogueId) === catalogueId
    );
    if (already) {
      setCatalogueActionHint('That sightseeing is already on this day.');
      return false;
    }
    addService({
      title: item.sightSeeingName || 'Sightseeing',
      kind: 'Sightseeing',
      sightseeingCatalogueId: catalogueId,
    });
    setCatalogueActionHint('');
    return true;
  };

  const removeService = (idx) => {
    const services = [...(day.services || [])];
    services.splice(idx, 1);
    updateDay({ services });
  };

  const updateLocalTransport = (patch) => {
    const ctxId = String(day.dayNumber);
    const list = [...(allTransports || [])];
    const idx = list.findIndex(
      (x) => x.context === 'local' && x.contextId === ctxId && x.dayNumber === day.dayNumber
    );
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch };
    } else {
      list.push({
        context: 'local',
        contextId: ctxId,
        dayNumber: day.dayNumber,
        mode: 'Cab · Sedan',
        chargeBasis: 'vehicle',
        capacity: 4,
        rateShared: 0,
        rateIndian: 0,
        rateForeign: 0,
        nationalityPricing: 'shared',
        enabled: true,
        ...patch,
      });
    }
    if (onTransportsChange) {
      onTransportsChange(list);
    }
  };

  const addIntercitySegment = () => {
    const legId = (day.legId || '').trim() || nextLegId(allDays);
    if (!day.legId) {
      onChange({ ...day, legId });
    }
    const hasLegRow = (allTransports || []).some(
      (t) => t.context === 'leg' && t.contextId === legId
    );
    if (!hasLegRow && onTransportsChange) {
      onTransportsChange([
        ...(allTransports || []),
        emptyLegTransport(legId, day.dayNumber),
      ]);
    }
  };

  const removeIntercitySegment = () => {
    const legId = (day.legId || '').trim();
    if (!legId) return;
    onChange({ ...day, legId: '' });
    if (onTransportsChange) {
      onTransportsChange(
        (allTransports || []).filter(
          (t) => !(t.context === 'leg' && t.contextId === legId)
        )
      );
    }
  };

  const legTransport = legTransports?.[0];
  const localTransport =
    localTransports?.[0] ||
    (allTransports || []).find(
      (x) =>
        x.context === 'local' &&
        x.contextId === String(day.dayNumber) &&
        x.dayNumber === day.dayNumber
    );
  const overnight = day.overnightCity || day.city;

  return (
    <div>
      <div className={dw.section}>
        <div className={dw.sectionHead}>
          <div>
            <h5>01 · Day details</h5>
            <p>Your existing itinerary data remains editable here.</p>
          </div>
          <span className={dw.tagGreen}>Day {day.dayNumber}</span>
        </div>
        <div className={dw.grid2}>
          <div className={dw.field}>
            <label className={dw.fieldLabel}>Day title</label>
            <Form.Control
              value={day.title || ''}
              onChange={(e) => updateDay({ title: e.target.value })}
              placeholder="Day title"
            />
          </div>
          <div className={dw.field}>
            <label className={dw.fieldLabel}>Overnight city</label>
            <div className={dw.fieldReadout}>
              {overnight || 'Departure day · no stay'}
            </div>
          </div>
        </div>
        <div className={dw.grid2} style={{ marginTop: 12 }}>
          <div className={dw.field}>
            <label className={dw.fieldLabel}>City (day label)</label>
            <Form.Control
              value={day.city || ''}
              onChange={(e) => updateDay({ city: e.target.value })}
            />
          </div>
          <div className={dw.field}>
            <label className={dw.fieldLabel}>Overnight city (edit)</label>
            <Form.Control
              value={day.overnightCity || ''}
              onChange={(e) => updateDay({ overnightCity: e.target.value })}
            />
          </div>
        </div>
        <div className={dw.field} style={{ marginTop: 12 }}>
          <label className={dw.fieldLabel}>Day description</label>
          <Form.Control
            as="textarea"
            rows={3}
            value={day.description || ''}
            onChange={(e) => updateDay({ description: e.target.value })}
          />
        </div>
      </div>

      <div className={dw.section}>
        <div className={dw.sectionHead}>
          <div>
            <h5>02 · Travel & local vehicle</h5>
            <p>Journey segments remain separate from local sightseeing transport.</p>
          </div>
        </div>
        <div className={dw.grid2}>
          {day.legId ? (
            <div className={dw.microcard}>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <strong>Intercity · {day.legId}</strong>
                  <p className={dw.foot} style={{ marginTop: 4, marginBottom: 0 }}>
                    Journey on this day · priced in Transport catalogue
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={removeIntercitySegment}
                >
                  Remove
                </Button>
              </div>
              {legTransport ? (
                <Row className="mt-2">
                  <Col md={6}>
                    <div className={dw.field}>
                      <label className={dw.fieldLabel}>Mode</label>
                      <Form.Control
                        as="select"
                        value={legTransport.mode || ''}
                        onChange={(e) => {
                          const list = [...allTransports];
                          const i = list.indexOf(legTransport);
                          if (i >= 0) {
                            list[i] = { ...list[i], mode: e.target.value };
                            onTransportsChange(list);
                          }
                        }}
                      >
                        {TRANSPORT_MODES.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className={dw.field}>
                      <label className={dw.fieldLabel}>Rate (shared ₹)</label>
                      <Form.Control
                        type="number"
                        value={legTransport.rateShared ?? 0}
                        onChange={(e) => {
                          const list = [...allTransports];
                          const i = list.indexOf(legTransport);
                          if (i >= 0) {
                            list[i] = {
                              ...list[i],
                              rateShared: Number(e.target.value),
                              rateIndian: Number(e.target.value),
                            };
                            onTransportsChange(list);
                          }
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className={dw.panelNote} style={{ marginTop: 8 }}>
                  No cab or vehicle option yet.{' '}
                  <Button size="sm" variant="link" className="p-0 align-baseline" onClick={addIntercitySegment}>
                    Add default option
                  </Button>{' '}
                  or use Transport catalogue → Intercity segments → Add option.
                </div>
              )}
            </div>
          ) : (
            <div className={dw.microcard}>
              <p className={dw.foot} style={{ marginBottom: 8 }}>
                No intercity journey on this day. Local transport is configured separately.
              </p>
              <Button size="sm" variant="outline-primary" onClick={addIntercitySegment}>
                Add intercity segment
              </Button>
            </div>
          )}

          <div className={dw.microcard}>
            <strong>{day.city || 'Local transport'}</strong>
            <p className={dw.foot} style={{ marginTop: 4 }}>
              Local transport · per vehicle or per seat
            </p>
            <Row className="mt-2">
              <Col md={6}>
                <div className={dw.field}>
                  <label className={dw.fieldLabel}>Mode</label>
                  <Form.Control
                    as="select"
                    value={localTransport?.mode || 'Cab · Sedan'}
                    onChange={(e) => updateLocalTransport({ mode: e.target.value })}
                  >
                    {TRANSPORT_MODES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Form.Control>
                </div>
              </Col>
              <Col md={6}>
                <div className={dw.field}>
                  <label className={dw.fieldLabel}>Charge basis</label>
                  <Form.Control
                    as="select"
                    value={localTransport?.chargeBasis || 'vehicle'}
                    onChange={(e) => updateLocalTransport({ chargeBasis: e.target.value })}
                  >
                    <option value="vehicle">Per vehicle</option>
                    <option value="person">Per person</option>
                  </Form.Control>
                </div>
              </Col>
              <Col md={4} className="mt-2">
                <div className={dw.field}>
                  <label className={dw.fieldLabel}>Capacity</label>
                  <Form.Control
                    type="number"
                    value={localTransport?.capacity ?? 4}
                    onChange={(e) => updateLocalTransport({ capacity: Number(e.target.value) })}
                  />
                </div>
              </Col>
              <Col md={4} className="mt-2">
                <div className={dw.field}>
                  <label className={dw.fieldLabel}>Rate ₹</label>
                  <Form.Control
                    type="number"
                    value={localTransport?.rateShared ?? 0}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      updateLocalTransport({ rateShared: v, rateIndian: v });
                    }}
                  />
                </div>
              </Col>
              <Col md={4} className="mt-2 d-flex align-items-end">
                <Form.Check
                  type="checkbox"
                  label="Include in pricing"
                  checked={localTransport?.enabled !== false}
                  onChange={(e) => updateLocalTransport({ enabled: e.target.checked })}
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>

      <div className={dw.section}>
        <div className={dw.sectionHead}>
          <div>
            <h5>03 · Sightseeing, activities & meals</h5>
            <p>Items from the saved itinerary — enter supplier rates here.</p>
          </div>
          <div className={dw.btnBar} style={{ margin: 0, gap: 8, flexWrap: 'wrap' }}>
            <Form.Control
              as="select"
              size="sm"
              style={{ maxWidth: 220 }}
              value={pickSightseeingId}
              onChange={(e) => {
                setPickSightseeingId(e.target.value);
                setCatalogueActionHint('');
              }}
              disabled={!sightseeings.length}
            >
              <option value="">Select sightseeing…</option>
              {sightseeings.map((item) => (
                <option key={item.id} value={String(item.id)}>
                  {item.sightSeeingName}
                </option>
              ))}
            </Form.Control>
            <Button
              size="sm"
              variant="outline-primary"
              disabled={!pickSightseeingId}
              onClick={() => {
                if (addSightseeingFromCatalogue(pickSightseeingId)) {
                  setPickSightseeingId('');
                }
              }}
            >
              Add sightseeing
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => addService({ kind: 'Activity', title: '' })}
            >
              Add activity
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => addService({ kind: 'Meal' })}>
              Add meal
            </Button>
          </div>
        </div>

        {!sightseeings.length && sightseeingsLoadHint && (
          <p className={`${dw.foot} text-muted`} style={{ marginBottom: 8 }}>
            {sightseeingsLoadHint} Use Add activity for fully manual items.
          </p>
        )}
        {catalogueActionHint && (
          <p className={`${dw.foot} text-warning`} style={{ marginBottom: 8 }}>
            {catalogueActionHint}
          </p>
        )}

        <div className={`${dw.subsection} ${dw.tablewrap}`}>
          <Table bordered size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Title</th>
                <th>Kind</th>
                <th>Nat.</th>
                <th>IN rate</th>
                <th>FN rate</th>
                <th>Child IN</th>
                <th>Child FN</th>
                <th>On</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(day.services || []).map((svc, idx) => (
                <tr key={idx}>
                  <td>
                    <Form.Control
                      size="sm"
                      value={svc.title || ''}
                      onChange={(e) => updateService(idx, { title: e.target.value })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={svc.kind || 'Sightseeing'}
                      onChange={(e) => updateService(idx, { kind: e.target.value })}
                    >
                      <option>Sightseeing</option>
                      <option>Activity</option>
                      <option>Meal</option>
                    </Form.Control>
                  </td>
                  <td>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={svc.nationalityMode || 'separate'}
                      onChange={(e) => updateService(idx, { nationalityMode: e.target.value })}
                    >
                      <option value="shared">Shared</option>
                      <option value="separate">Separate</option>
                    </Form.Control>
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={svc.rateIndian ?? 0}
                      onChange={(e) => updateService(idx, { rateIndian: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={svc.rateForeign ?? 0}
                      onChange={(e) => updateService(idx, { rateForeign: Number(e.target.value) })}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={svc.childRateIndian ?? 0}
                      onChange={(e) =>
                        updateService(idx, { childRateIndian: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={svc.childRateForeign ?? 0}
                      onChange={(e) =>
                        updateService(idx, { childRateForeign: Number(e.target.value) })
                      }
                    />
                  </td>
                  <td>
                    <Form.Check
                      checked={svc.enabled !== false}
                      onChange={(e) => updateService(idx, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    <Button size="sm" variant="outline-danger" onClick={() => removeService(idx)}>
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
              {(day.services || []).length === 0 && (
                <tr>
                  <td colSpan={9} className="text-muted text-center">
                    No services yet — add sightseeing, activities, or meals above.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <div className={dw.section}>
        <div className={dw.btnBar}>
          <Button variant="outline-secondary" disabled={dayIndex <= 0} onClick={onPrev}>
            ← Previous day
          </Button>
          <Button variant="primary" disabled={dayIndex >= totalDays - 1} onClick={onNext}>
            {dayIndex >= totalDays - 1 ? 'Last day' : 'Next day →'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DayEditor;
